import {TauroAccountFactorisation, getTauroFactorisationFromAccount} from '../../../common/utils/accounts'
import {Account} from '../../../graphql/types'

export type FundedAccountPosition = {
  startingBalance: number
  floor: number
  /** Null when the live equity is unknown. The tab then shows no distance and no bar. */
  current: number | null
  /** Amount the account can still lose before the floor, never below zero. */
  distanceToFloor: number | null
  /** Where the starting allocation sits on the bar from the floor to the top, in percent. */
  startPercent: number | null
  /** Where the current equity sits on the same bar, in percent. */
  currentPercent: number | null
}

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value))

/**
 * Places the current equity of a funded factorised account between the equity floor and
 * the higher of the starting allocation and the equity itself (PT-7656). Returns null while
 * the account has no funding record, because there is no allocation or floor to show yet.
 * An unknown equity gives no current value, distance or bar: a guessed number would hide a
 * loss next to the floor. The floor is the line where the liquidation starts, so it is the left end of the bar.
 */
export const getFundedAccountPosition = (
  factorisation: TauroAccountFactorisation | null,
  equity: number | null | undefined,
): FundedAccountPosition | null => {
  const startingBalance = factorisation?.startingBalance
  const floor = factorisation?.equityFloor
  if (startingBalance == null || floor == null) return null

  if (equity == null || isNaN(equity)) {
    return {startingBalance, floor, current: null, distanceToFloor: null, startPercent: null, currentPercent: null}
  }

  const current = equity
  const top = Math.max(startingBalance, current)
  const span = top - floor
  if (span <= 0) return null

  return {
    startingBalance,
    floor,
    current,
    distanceToFloor: Math.max(0, current - floor),
    startPercent: clampPercent(((startingBalance - floor) / span) * 100),
    currentPercent: clampPercent(((current - floor) / span) * 100),
  }
}

/**
 * The live equity of the account, or null when the margin data is not available. The stored
 * balance is not a fallback: it leaves out the open positions, and the floor works on equity.
 */
export const getFundedAccountEquity = (account: Account): number | null => {
  const equity = (account as {marginInfo?: {equity?: number | null}}).marginInfo?.equity
  return equity != null && !isNaN(equity) ? equity : null
}

/**
 * The factorised accounts of the client that the platform closed or the client archived,
 * newest first, so the Funded tab can show what they did before (PT-7656).
 */
export const getClosedFactorisedAccounts = (accounts: Account[], currentAccountId: number): Account[] =>
  accounts
    .filter(
      (account) =>
        (account as {id?: number}).id !== currentAccountId &&
        (account as {isArchived?: boolean}).isArchived === true &&
        getTauroFactorisationFromAccount(account) != null,
    )
    .sort((a, b) => Number((b as {id?: number}).id) - Number((a as {id?: number}).id))

/**
 * True for an account that the platform settled, liquidated or archived. Its live equity is 0
 * after the payout, so a distance to the floor and a bar would show a false picture (PT-7656).
 */
export const isFundedAccountClosed = (account: Account): boolean =>
  (account as {isArchived?: boolean}).isArchived === true
